import { Injectable } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { OCC_USER_ID_ANONYMOUS, TranslationService, UserIdService } from '@spartacus/core'
import { combineLatest, Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { EbFormConfig } from '../../../../form/interfaces/eb-form.interface'
import { EbCommonFormLoginHintComponent } from '../../../common/components/common-form-login-hint/eb-common-form-login-hint.component'
import { EbCommonFormConfigService } from '../../../common/services/facades/eb-common-form-config.service'
import { EbContactFormRfpHintComponent } from '../../components/contact-form-rfp-hint/eb-contact-form-rfp-hint.component'
import { EbContactFormTokenEnum } from '../../interfaces/eb-contact-form.interface'
import { EbContactFormAdapter } from '../connectors/eb-contact-form.adapter'

@Injectable()
export class EbContactFormConfigService {
  constructor(
    protected adapter: EbContactFormAdapter,
    protected commonFormConfigService: EbCommonFormConfigService,
    protected userIdService: UserIdService,
    protected route: ActivatedRoute,
    protected translationService: TranslationService
  ) {}

  get formConfig$(): Observable<EbFormConfig> {
    return this.formConfigFactory()
  }

  private formConfigFactory(): Observable<EbFormConfig> {
    return combineLatest([
      this.commonFormConfigService.getPersonalInfoGroupConfig(),
      this.commonFormConfigService.getPrivacyAndCaptchaFieldsConfig(),
      this.adapter.getContactUsFormTopics(),
      this.userIdService.getUserId(),
      this.getUrlToken(),
      this.getRequestShippingAddressMessage()
    ]).pipe(
      map(
        ([
          personalInfoGroupConfig,
          privacyAndCaptchaFieldsConfig,
          topics,
          userId,
          contactToken,
          contactMessage
        ]) => {
          const personalInfoElements = [...personalInfoGroupConfig.elements]
          if (userId === OCC_USER_ID_ANONYMOUS) {
            personalInfoElements.unshift({
              name: 'login-hint',
              hideTitle: true,
              type: 'component',
              component: EbCommonFormLoginHintComponent
            })
          }
          return {
            steps: [
              {
                hideTitle: true,
                elements: [
                  {
                    name: 'rfp-hint',
                    hideTitle: true,
                    type: 'component',
                    component: EbContactFormRfpHintComponent
                  },
                  {
                    name: 'topicId',
                    type: 'field',
                    value: contactToken === EbContactFormTokenEnum.SHIPPINGADDRESS ? 'account' : '',
                    fieldType: 'select',
                    required: true,
                    options: topics.map(topic => ({
                      name: topic.id,
                      label: {
                        default: topic.name
                      }
                    }))
                  },
                  {
                    name: 'message',
                    type: 'field',
                    value:
                      contactToken === EbContactFormTokenEnum.SHIPPINGADDRESS ? contactMessage : '',
                    fieldType: 'textarea'
                  },
                  {
                    name: 'attachment',
                    type: 'field',
                    value: '',
                    fieldType: 'filedrop',
                    meta: {
                      accept: '.pdf, .jpg, .jpeg, .png',
                      maxFileSize: '25MB'
                    }
                  },
                  {
                    ...personalInfoGroupConfig,
                    hideTitle: false,
                    elements: personalInfoElements
                  },
                  ...privacyAndCaptchaFieldsConfig
                ]
              }
            ],
            stepsAlwaysEnabled: true
          }
        }
      )
    )
  }

  getUrlToken(): Observable<EbContactFormTokenEnum> {
    return this.route.queryParams.pipe(map(params => params[Object.keys(params)[0]]))
  }

  getRequestShippingAddressMessage(): Observable<string> {
    return this.translationService.translate('contact.message.requestShippingAddress')
  }
}
